import { formatDate, getFullName, timeFromNow } from '@utils';
import { FILTER_OPERATOR_ENUMS, FILTER_RULE_ENUMS } from '@constants';
import {
  CREDIT_CHECK_TABLE_STAGES,
  CREDIT_CHECK_TABLE_STAGES_ORDER,
} from 'components/creditCheckTable/constants';
import { groupBy, pick } from 'lodash-es';
import { URLS } from 'layouts/DrawerLayout/constants';

export const handleCreditCheckTableData = (data = [], assignees = []) => {
  const assigneesMap = assignees.reduce((acc, cur) => {
    acc[cur.value] = cur;
    return acc;
  }, {});

  return data.map((creditCheck) => {
    const date = creditCheck?.createdAt
      ? formatDate(creditCheck?.createdAt, 'dd/MM/yyyy')
      : '-';
    const contractPeriod = creditCheck?.contractPeriod
      ? `${creditCheck.contractPeriod} Month${
          creditCheck.contractPeriod > 1 ? 's' : ''
        }`
      : '-';
    const assignee = assigneesMap[creditCheck?.assignee];
    const bookingOwnerType =
      creditCheck?.bookingAccountType ||
      creditCheck?.bookingOwnerType ||
      creditCheck?.bookingOwner?.type ||
      creditCheck?.ownerType ||
      '';
    const normalizedBookingOwnerType = String(bookingOwnerType)
      .trim()
      .toLowerCase();
    let bookingAccountType = '-';
    if (
      normalizedBookingOwnerType === 'b2b' ||
      normalizedBookingOwnerType === 'company'
    ) {
      bookingAccountType = 'B2B';
    } else if (
      normalizedBookingOwnerType === 'b2c' ||
      normalizedBookingOwnerType === 'individual'
    ) {
      bookingAccountType = 'B2C';
    }

    const rawCustomerType =
      creditCheck?.customerType || creditCheck?.customer?.type || '';
    const customerTypeValue = String(rawCustomerType).trim().toLowerCase();
    let customerType = '-';
    if (customerTypeValue === 'existing') {
      customerType = 'Existing';
    } else if (customerTypeValue === 'new') {
      customerType = 'New';
    }

    return {
      id: creditCheck?.id,
      bookingId: creditCheck?.bookingId,
      stage: creditCheck.stage,
      date,
      vehicleTenant: creditCheck?.vehicle?.tenant || '-',
      customerType,
      bookingAccountType,
      assignee,
      skuId: creditCheck?.vehicle?.id || '-',
      fullName: getFullName(creditCheck),
      title: getFullName(creditCheck),
      createdAt: date,
      timeAgo: timeFromNow(creditCheck?.createdAt),
      email: creditCheck?.email,
      contractPeriod,
      contractType: creditCheck?.contractType || '-',
      phoneNumber: creditCheck?.phoneNumber || '-',
      origin: creditCheck?.origin || '-',
      initialPayment: creditCheck?.initialPayment || '-',
      vehicle: creditCheck?.vehicle?.title || '-',
      userId: creditCheck?.userId,
      labels: creditCheck?.labels || [],
      href: `${URLS.bookings}/${creditCheck?.bookingId}`,
    };
  });
};

export const getCreditCheckTableCardColumns = (tasks = []) => {
  const order = CREDIT_CHECK_TABLE_STAGES_ORDER;
  const groupedByStatus = groupBy(tasks, 'stage');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: CREDIT_CHECK_TABLE_STAGES[cur]?.label,
      cards: (groupedByStatus[cur] || []).map((card) => ({
        ...card,
        id: card.bookingId ?? card.id,
      })),
      styles: CREDIT_CHECK_TABLE_STAGES[cur]?.styles,
      value: true,
      label: CREDIT_CHECK_TABLE_STAGES[cur]?.label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

export const getCreditCheckQueryParams = (payload = {}) => {
  return pick(payload, ['page', 'pageSize', 'order', 'orderBy', 'query']);
};

export const getCreditCheckPayload = (payload = {}) => {
  const fields = pick(payload, ['origin', 'stage', 'contractType']);

  const multiFilterFields = ['origin', 'stage', 'contractType'];
  const numericMultiFilterFields = ['stage'];

  const fieldsMap = {
    origin: 'origin',
    stage: 'stage',
    contractType: 'contractType',
    dateFrom: 'createdAt',
    dateTo: 'createdAt',
  };

  const value = Object.keys(fields).map((field) => {
    let ruleValue = multiFilterFields.includes(field)
      ? String(payload[field]).split(',').filter(Boolean)
      : payload[field];

    if (numericMultiFilterFields.includes(field) && Array.isArray(ruleValue)) {
      ruleValue = ruleValue.map(Number);
    }

    return {
      field: fieldsMap[field] || field,
      operator: FILTER_OPERATOR_ENUMS['='],
      type: multiFilterFields.includes(field)
        ? FILTER_RULE_ENUMS.IsAnyOfRuleType
        : FILTER_RULE_ENUMS.ComparableRuleType,
      value: ruleValue,
    };
  });

  const { dateFrom, dateTo } = payload;
  if (dateFrom && dateTo) {
    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);

    value.push({
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: fieldsMap.dateFrom,
          operator: FILTER_OPERATOR_ENUMS['>='],
          value: startDate.getTime(),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: fieldsMap.dateTo,
          operator: FILTER_OPERATOR_ENUMS['<='],
          value: endDate.getTime(),
        },
      ],
    });
  }

  if (!value.length) {
    return {
      type: FILTER_RULE_ENUMS.EmptyRuleType,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value,
  };
};
